import {
  Check,
  ChevronDown,
  ChevronLeft,
  MapPin,
  MinusCircle,
  Plus,
  FileText,
  Image as ImageIcon,
  File as FileIcon,
  Download,
  Loader2,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import tarrantCountyZipCodes from "../../data/tarrant-county-zip-codes.json";
import ReactPaginate from "react-paginate";
import toast from "react-hot-toast";
import { BASE_URL, API_ENDPOINTS } from "../../config/api.config";
import {
  ALLOWED_COVER_IMAGE_TYPES,
  ALLOWED_EVENT_SUPPORTING_FILES_TYPES,
  validateFileSize,
  validateFileType,
  formatFileSize,
} from "../../config/fileConfig";
import SendCollaborationRequestModal from "./send-collaboration-request-modal";
import LocationPickerModal from "./LocationPickerModal";
import { Link } from "react-router-dom";

const itemsPerPage = 3;

const createRoleId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `role_${Math.random().toString(36).slice(2, 10)}`;
};

const DEFAULT_FORM_STATE = {
  title: "",
  typeCode: "",
  description: "",
  date: "",
  time: "",
  location: "",
  latitude: null,
  longitude: null,
  zipCode: "",
  resourceRequirements: "",
  needVolunteers: false,
  inviteNgoMunicipality: false,
};

const PostEventForm = ({
  initialValues,
  initialRoles,
  onSubmit,
  onSaveDraft,
  submitLabel,
  mode = "create",
  isSubmitting = false,
  eventId = null,
}) => {
  // Files state: stores attachment objects {url, name, id?} for existing/uploads
  const [files, setFiles] = useState([]);
  // FilesToUpload: stores File objects that need to be uploaded
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [coverImage, setCoverImage] = useState(null);
  const [coverImageUrl, setCoverImageUrl] = useState(
    initialValues?.coverImageUrl || null,
  );
  const [errors, setErrors] = useState({
    title: false,
    typeCode: false,
    description: false,
    date: false,
    time: false,
    location: false,
    zipCode: false,
    coverImage: false,
  });
  const [isUploadingCoverImage, setIsUploadingCoverImage] = useState(false);
  const [coverImageError, setCoverImageError] = useState(false);
  const [previewImageError, setPreviewImageError] = useState(false);
  const [form, setForm] = useState({ ...DEFAULT_FORM_STATE, ...initialValues });
  const [currentPage, setCurrentPage] = useState(0);
  const [roleDraft, setRoleDraft] = useState({
    roleName: "",
    countNeeded: "",
    quantityType: "Unit",
    specialRequirements: "",
  });
  const [roles, setRoles] = useState(
    () =>
      (initialRoles || []).map((role) => ({
        id: role.id || createRoleId(),
        roleName: role.roleName,
        countNeeded: role.countNeeded,
        quantityType: role.quantityType || "Unit",
        specialRequirements: role.specialRequirements || "",
      })) || [],
  );
  const [nonprofits, setNonprofits] = useState([]);
  const [sentRecipientIds, setSentRecipientIds] = useState([]);
  const [zipCodeSearch, setZipCodeSearch] = useState(initialValues?.zipCode || "");
  const [isZipCodeDropdownOpen, setIsZipCodeDropdownOpen] = useState(false);
  const zipCodeDropdownRef = useRef(null);
  const [isLoadingNonprofits, setIsLoadingNonprofits] = useState(false);
  const [selectedNonprofitForModal, setSelectedNonprofitForModal] =
    useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [currentUserNonprofitId, setCurrentUserNonprofitId] = useState(null);
  const [collaborationRequests, setCollaborationRequests] = useState([]);
  const [isLoadingRequests, setIsLoadingRequests] = useState(false);
  const [categories, setCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [collabRequestsEnabled, setCollabRequestsEnabled] = useState(true);

  // Fetch user preference for collaboration requests
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;
    fetch(`${BASE_URL}${API_ENDPOINTS.NONPROFIT_GET_PROFILE}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((result) => {
        const prefs = result?.data?.preferences;
        if (prefs && prefs.collaborationRequests === false) {
          setCollabRequestsEnabled(false);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (initialValues) {
      const hasRoles = Array.isArray(initialRoles) && initialRoles.length > 0;
      setForm({
        ...DEFAULT_FORM_STATE,
        ...initialValues,
        needVolunteers: initialValues.needVolunteers || hasRoles,
      });
      if (initialValues.zipCode) {
        setZipCodeSearch(initialValues.zipCode);
      }
      if (initialValues.coverImageUrl) {
        // If it's a Cloudinary URL, set it directly
        // If it's a data URL, it's a preview from a file
        setCoverImageUrl(initialValues.coverImageUrl);
        setCoverImageError(false);
        setPreviewImageError(false);
        // Clear any local file since we're loading from initial values
        setCoverImage(null);
      }
      // Load existing attachments
      if (initialValues.attachments) {
        const normalizedAttachments = Array.isArray(initialValues.attachments)
          ? initialValues.attachments
              .map((att) => {
                // If it's already an object with url and name, use it
                if (typeof att === "object" && att !== null && att.url) {
                  return {
                    url: att.url,
                    name: att.name || att.url.split("/").pop() || "file",
                    id: att.id,
                  };
                }
                // If it's a string (URL), normalize to object
                if (typeof att === "string") {
                  return {
                    url: att,
                    name: att.split("/").pop() || "file",
                  };
                }
                return null;
              })
              .filter(Boolean)
          : [];
        setFiles(normalizedAttachments);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  useEffect(() => {
    if (initialRoles) {
      setRoles(
        initialRoles.map((role) => ({
          id: role.id || createRoleId(),
          roleName: role.roleName,
          countNeeded: role.countNeeded,
          quantityType: role.quantityType || "Unit",
          specialRequirements: role.specialRequirements || "",
        })),
      );
    }
  }, [initialRoles]);

  // Close zip code dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (zipCodeDropdownRef.current && !zipCodeDropdownRef.current.contains(e.target)) {
        setIsZipCodeDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Fetch user services (categories) on component mount
  useEffect(() => {
    const fetchUserServices = async () => {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        return;
      }

      setIsLoadingCategories(true);
      try {
        const response = await fetch(
          `${BASE_URL}${API_ENDPOINTS.GET_USER_SERVICES}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (response.ok) {
          const result = await response.json();
          const services = result?.data || result;
          setCategories(Array.isArray(services) ? services : []);
        } else {
          console.error("Failed to fetch user services");
        }
      } catch (error) {
        console.error("Error fetching user services:", error);
      } finally {
        setIsLoadingCategories(false);
      }
    };

    fetchUserServices();
  }, []);

  const uploadCoverImage = async () => {
    // If coverImage is a File object (new upload), upload it
    if (coverImage instanceof File) {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        throw new Error("Session expired. Please sign in again.");
      }

      setIsUploadingCoverImage(true);
      try {
        const formData = new FormData();
        formData.append("file", coverImage);

        const response = await fetch(
          `${BASE_URL}${API_ENDPOINTS.UPLOAD_IMAGE}`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          },
        );

        const result = await response.json().catch(() => null);
        const statusCode = result?.statusCode ?? response.status;

        if (!response.ok || statusCode >= 400) {
          const message = result?.message || "Failed to upload cover image";
          throw new Error(message);
        }

        const uploadedUrl = result?.data?.url || result?.url;
        if (uploadedUrl) {
          setCoverImageUrl(uploadedUrl);
          return uploadedUrl;
        } else {
          throw new Error("No URL returned from upload");
        }
      } catch (error) {
        console.error("Cover image upload error:", error);
        throw error;
      } finally {
        setIsUploadingCoverImage(false);
      }
    }

    // If coverImageUrl is already a Cloudinary URL (existing or just uploaded), return it
    if (
      coverImageUrl &&
      typeof coverImageUrl === "string" &&
      coverImageUrl.startsWith("http")
    ) {
      return coverImageUrl;
    }

    // If no cover image, return undefined
    return undefined;
  };

  const uploadSupportingFiles = async () => {
    if (!filesToUpload || filesToUpload.length === 0) {
      // Return existing files as objects with url and name
      return files.map((f) => ({ url: f.url, name: f.name }));
    }

    const token = localStorage.getItem("accessToken");
    if (!token) {
      throw new Error("Session expired. Please sign in again.");
    }

    setIsUploadingFiles(true);
    const uploadedFiles = [];

    try {
      // Upload each file sequentially
      for (const file of filesToUpload) {
        const formData = new FormData();
        formData.append("file", file);

        const response = await fetch(
          `${BASE_URL}${API_ENDPOINTS.UPLOAD_IMAGE}`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          },
        );

        const result = await response.json().catch(() => null);
        const statusCode = result?.statusCode ?? response.status;

        if (!response.ok || statusCode >= 400) {
          const message = result?.message || `Failed to upload ${file.name}`;
          throw new Error(message);
        }

        const uploadedUrl = result?.data?.url || result?.url;
        if (uploadedUrl) {
          // Store both URL and original filename
          uploadedFiles.push({
            url: uploadedUrl,
            name: file.name, // Preserve original filename
          });
        } else {
          throw new Error(`No URL returned from upload for ${file.name}`);
        }
      }

      // Calculate all files (existing + newly uploaded) before updating state
      const existingFiles = files.map((f) => ({ url: f.url, name: f.name }));
      const allFiles = [...existingFiles, ...uploadedFiles];

      // Update files state with newly uploaded files so they persist in UI
      // Use functional update to ensure we have the latest state
      setFiles((prev) => {
        const prevFiles = prev.map((f) => ({ url: f.url, name: f.name }));
        return [...prevFiles, ...uploadedFiles];
      });

      // Clear filesToUpload after successful upload
      setFilesToUpload([]);

      return allFiles;
    } catch (error) {
      console.error("Supporting files upload error:", error);
      throw error;
    } finally {
      setIsUploadingFiles(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const newErrors = {
      title: !form.title?.trim(),
      typeCode: !form.typeCode,
      description: !form.description?.trim(),
      date: !form.date,
      time: !form.time,
      location: !form.location?.trim(),
      zipCode: !form.zipCode,
      coverImage: !coverImageUrl,
    };

    setErrors(newErrors);
    const missingFields = Object.entries(newErrors)
      .filter(([, v]) => v)
      .map(([k]) => {
        const labels = {
          title: "Event Title",
          typeCode: "Category",
          description: "Description",
          date: "Date",
          time: "Time",
          location: "Location",
          zipCode: "ZIP Code",
          coverImage: "Cover Image",
        };
        return labels[k] || k;
      });

    if (missingFields.length === 1) {
      toast.error(`Please fill the required field: ${missingFields[0]}`);
      return;
    } else if (missingFields.length > 1) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      // Upload cover image if there's a new file
      const uploadedCoverImageUrl = await uploadCoverImage();

      // Upload supporting files if there are any to upload
      const attachmentUrls = await uploadSupportingFiles();

      // Remove typeCode from form data and use categoryValue instead
      const { typeCode, ...formWithoutTypeCode } = form;
      const payload = {
        ...formWithoutTypeCode,
        // Map typeCode to categoryValue for new category system
        categoryValue: form.typeCode || undefined, // Only send if not empty
        roles: roles.map(({ roleName, countNeeded, specialRequirements }) => ({
          roleName,
          countNeeded,
          specialRequirements,
        })),
        attachments: attachmentUrls,
        coverImageUrl: uploadedCoverImageUrl,
      };

      if (typeof onSubmit === "function") {
        onSubmit(payload);
      }
    } catch (error) {
      toast.error(
        error?.message || "Failed to upload files. Please try again.",
      );
    }
  };

  const handleSaveDraft = async (event) => {
    event.preventDefault();

    // Validate category is selected (required even for drafts)
    if (!form.typeCode) {
      toast.error("Please select a category for the event");
      return;
    }

    try {
      // Upload cover image if there's a new file
      const uploadedCoverImageUrl = await uploadCoverImage();

      // Upload supporting files if there are any to upload
      const attachmentUrls = await uploadSupportingFiles();

      // Remove typeCode from form data and use categoryValue instead
      const { typeCode, ...formWithoutTypeCode } = form;
      const payload = {
        ...formWithoutTypeCode,
        // Map typeCode to categoryValue for new category system
        categoryValue: form.typeCode || undefined, // Only send if not empty
        roles: roles.map(({ roleName, countNeeded, specialRequirements }) => ({
          roleName,
          countNeeded,
          specialRequirements,
        })),
        attachments: attachmentUrls,
        coverImageUrl: uploadedCoverImageUrl,
        isDraft: true,
      };

      if (typeof onSaveDraft === "function") {
        onSaveDraft(payload);
      }
    } catch (error) {
      toast.error(
        error?.message || "Failed to upload files. Please try again.",
      );
    }
  };

  const handleFiles = (selectedFiles) => {
    const fileArray = Array.from(selectedFiles || []);
    const validFiles = [];

    for (const file of fileArray) {
      // Validate file type
      const typeValidation = validateFileType(
        file,
        ALLOWED_EVENT_SUPPORTING_FILES_TYPES,
      );
      if (!typeValidation.valid) {
        toast.error(`${file.name}: ${typeValidation.error}`);
        continue;
      }

      // Validate file size
      const sizeValidation = validateFileSize(file);
      if (!sizeValidation.valid) {
        toast.error(`${file.name}: ${sizeValidation.error}`);
        continue;
      }

      validFiles.push(file);
    }

    if (validFiles.length > 0) {
      setFilesToUpload((prev) => [...prev, ...validFiles]);
    }
  };

  // const handleDrop = (event) => {
  //   event.preventDefault();
  //   handleFiles(event.dataTransfer.files);
  //   event.currentTarget.classList.remove("border-[#3FC4E3]", "bg-[#f9feff]");
  // };

  // const handleDragOver = (event) => {
  //   event.preventDefault();
  //   event.currentTarget.classList.add("border-[#3FC4E3]", "bg-[#f9feff]");
  // };

  // const handleDragLeave = (event) => {
  //   event.currentTarget.classList.remove("border-[#3FC4E3]", "bg-[#f9feff]");
  // };

  const handleRemoveFileToUpload = (index) => {
    setFilesToUpload((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCoverImageSelect = (selectedFile) => {
    if (!selectedFile) return;

    // Validate file type
    const typeValidation = validateFileType(
      selectedFile,
      ALLOWED_COVER_IMAGE_TYPES,
    );
    if (!typeValidation.valid) {
      toast.error(typeValidation.error);
      return;
    }

    // Validate file size
    const sizeValidation = validateFileSize(selectedFile);
    if (!sizeValidation.valid) {
      toast.error(sizeValidation.error);
      return;
    }

    // Store file locally and create preview
    setCoverImage(selectedFile);

    // Create preview URL from file
    const reader = new FileReader();
    reader.onloadend = () => {
      setCoverImageUrl(reader.result);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleCoverImageDrop = (event) => {
    event.preventDefault();
    event.currentTarget.classList.remove("border-[#3FC4E3]", "bg-[#f9feff]");
    const droppedFile = event.dataTransfer.files[0];
    if (droppedFile) {
      handleCoverImageSelect(droppedFile);
    }
  };

  const handleCoverImageDragOver = (event) => {
    event.preventDefault();
    event.currentTarget.classList.add("border-[#3FC4E3]", "bg-[#f9feff]");
  };

  const handleCoverImageDragLeave = (event) => {
    event.currentTarget.classList.remove("border-[#3FC4E3]", "bg-[#f9feff]");
  };

  const handleRemoveCoverImage = () => {
    setCoverImage(null);
    // If coverImageUrl is a data URL (preview), clear it
    // If it's a Cloudinary URL (existing), we still want to clear it to remove the image
    setCoverImageUrl(null);
    setCoverImageError(false);
    setPreviewImageError(false);
  };

  useEffect(() => {
    // Reset error states when coverImageUrl changes
    if (coverImageUrl) {
      setCoverImageError(false);
      setPreviewImageError(false);
    }
  }, [coverImageUrl]);

  const handleResourceFiles = (selectedFiles) => {
    handleFiles(selectedFiles);
  };

  const handleResourceDrop = (event) => {
    event.preventDefault();
    handleFiles(event.dataTransfer.files);
    event.currentTarget.classList.remove("border-[#3FC4E3]", "bg-[#f9feff]");
  };

  const handleResourceDragOver = (event) => {
    event.preventDefault();
    event.currentTarget.classList.add("border-[#3FC4E3]", "bg-[#f9feff]");
  };

  const handleDragLeaveResource = (event) => {
    event.currentTarget.classList.remove("border-[#3FC4E3]", "bg-[#f9feff]");
  };

  const handleFormChange = (field) => (event) => {
    const value =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));

    // Reset pagination when category changes
    if (field === "typeCode") {
      setCurrentPage(0);
    }
  };

  const handleRoleDraftChange = (field) => (event) => {
    setRoleDraft((prev) => ({
      ...prev,
      [field]:
        field === "countNeeded"
          ? event.target.value.replace(/\D/g, "")
          : event.target.value,
    }));
  };

  const handleAddRole = () => {
    if (!roleDraft.countNeeded || !roleDraft.roleName) {
      return;
    }

    setRoles((prev) => [
      ...prev,
      {
        id: createRoleId(),
        roleName: roleDraft.roleName,
        countNeeded: Number(roleDraft.countNeeded),
        quantityType: roleDraft.quantityType || "Unit",
        specialRequirements: roleDraft.specialRequirements.trim(),
      },
    ]);

    setRoleDraft((prev) => ({
      ...prev,
      countNeeded: "",
      specialRequirements: "",
    }));
  };

  const handleRemoveRole = (id) => {
    setRoles((prev) => prev.filter((role) => role.id !== id));
  };

  const roleNameDisplay = useMemo(() => {
    const displayMap = categories.reduce((acc, category) => {
      acc[category.value] = category.name;
      return acc;
    }, {});
    // Helper function to get display name
    return (roleName) => {
      return displayMap[roleName] || "Other";
    };
  }, [categories]);

  const actionLabel =
    submitLabel || (mode === "edit" ? "Update Event" : "Post Event");
  const isUploading = isUploadingFiles || isUploadingCoverImage;
  const actionInProgressLabel =
    isSubmitting || isUploading
      ? submitLabel
        ? `${submitLabel}${submitLabel.endsWith("...") ? "" : "..."}`
        : mode === "edit"
          ? "Saving..."
          : "Posting..."
      : actionLabel;

  useEffect(() => {
    // Fetch current user's nonprofit ID and nonprofits on mount
    fetchCurrentUserNonprofit();
    fetchNonprofits();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Refetch nonprofits when we get the current user's nonprofit ID to filter it out
    if (currentUserNonprofitId !== null) {
      fetchNonprofits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserNonprofitId]);

  const fetchCurrentUserNonprofit = async () => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;

    try {
      const response = await fetch(`${BASE_URL}users/me`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json().catch(() => null);
      const statusCode = result?.statusCode ?? response.status;

      if (response.ok && statusCode < 400 && result?.data?.nonProfitUser?.id) {
        setCurrentUserNonprofitId(result.data.nonProfitUser.id);
      }
    } catch (error) {
      console.error("Fetch current user nonprofit error:", error);
      // Silently fail - we'll just show all nonprofits if we can't get the current user's ID
    }
  };

  useEffect(() => {
    // Fetch existing collaboration request for this event if eventId is available
    if (eventId || (mode === "edit" && initialValues?.id)) {
      fetchExistingCollaborationRequest();
      fetchCollaborationRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, mode, initialValues?.id]);

  // Auto-enable the invite toggle when sent collaboration requests exist
  useEffect(() => {
    if (sentRecipientIds.length > 0) {
      setForm((prev) => ({ ...prev, inviteNgoMunicipality: true }));
    }
  }, [sentRecipientIds]);

  const fetchNonprofits = async () => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;

    setIsLoadingNonprofits(true);
    try {
      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.NONPROFIT_DIRECTORY}?page=1&pageSize=100`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const result = await response.json().catch(() => null);
      const statusCode = result?.statusCode ?? response.status;

      if (!response.ok || statusCode >= 400) {
        throw new Error(result?.message || "Failed to fetch nonprofits");
      }

      // Handle nested response structure: result.data.data contains the array
      const payload = result?.data ?? null;
      const nonprofitsList = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
          ? payload
          : Array.isArray(result?.data)
            ? result.data
            : [];

      // Filter out current user's nonprofit from the list (if we have the ID)
      const filteredList = currentUserNonprofitId
        ? nonprofitsList.filter(
            (nonprofit) => nonprofit.id !== currentUserNonprofitId,
          )
        : nonprofitsList;
      setNonprofits(filteredList);
    } catch (error) {
      console.error("Fetch nonprofits error:", error);
      toast.error(error?.message || "Failed to load nonprofits");
      setNonprofits([]);
    } finally {
      setIsLoadingNonprofits(false);
    }
  };

  const fetchExistingCollaborationRequest = async () => {
    const currentEventId = eventId || initialValues?.id;
    if (!currentEventId) return;

    const token = localStorage.getItem("accessToken");
    if (!token) return;

    try {
      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.NONPROFIT_GET_COLLABORATION_REQUEST_BY_EVENT}/${currentEventId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const result = await response.json().catch(() => null);
      const statusCode = result?.statusCode ?? response.status;

      if (response.ok && statusCode < 400 && result?.data) {
        // Extract recipient IDs that have already been sent requests
        const recipientIds =
          result.data.recipients?.map((r) => r.nonprofit?.id).filter(Boolean) ||
          [];
        setSentRecipientIds(recipientIds);
      }
    } catch (error) {
      console.error("Fetch existing collaboration request error:", error);
      // Silently fail - it's okay if there's no existing request
    }
  };

  const handleOpenModal = (nonprofit) => {
    const currentEventId = eventId || initialValues?.id;
    if (!currentEventId) {
      toast.error(
        "Please save the event first before sending collaboration requests",
      );
      return;
    }

    if (sentRecipientIds.includes(nonprofit.id)) {
      toast.error("Request has already been sent to this nonprofit");
      return;
    }

    setSelectedNonprofitForModal(nonprofit);
    setIsModalOpen(true);
  };

  const handleModalSuccess = () => {
    if (selectedNonprofitForModal?.id) {
      setSentRecipientIds((prev) => [...prev, selectedNonprofitForModal.id]);
    }
    setCurrentPage(0); // Reset to first page to see next available nonprofit
    fetchExistingCollaborationRequest(); // Refresh to get updated data
    fetchCollaborationRequests(); // Refresh collaboration requests task list
  };

  const fetchCollaborationRequests = async () => {
    const currentEventId = eventId || initialValues?.id;
    if (!currentEventId) return;

    const token = localStorage.getItem("accessToken");
    if (!token) return;

    setIsLoadingRequests(true);
    try {
      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.NONPROFIT_GET_COLLABORATION_REQUEST_BY_EVENT}/${currentEventId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const result = await response.json().catch(() => null);
      const statusCode = result?.statusCode ?? response.status;

      if (response.ok && statusCode < 400 && result?.data) {
        // Extract all recipients with their tasks
        const recipients = result.data.recipients || [];
        setCollaborationRequests(recipients);
      } else {
        // No collaboration request exists for this event, which is fine
        setCollaborationRequests([]);
      }
    } catch (error) {
      console.error("Fetch collaboration requests error:", error);
      // Silently fail - it's okay if there's no collaboration request
      setCollaborationRequests([]);
    } finally {
      setIsLoadingRequests(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "ACCEPTED":
        return "bg-[#3FE36B]";
      case "REJECTED":
        return "bg-red-500";
      case "PENDING":
        return "bg-[#DFCD00]";
      default:
        return "bg-gray-500";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "ACCEPTED":
        return "Accepted";
      case "REJECTED":
        return "Rejected";
      case "PENDING":
        return "Pending";
      default:
        return status || "Pending";
    }
  };

  // Filter and sort nonprofits: filter by category if selected, then sort by sent status
  const sortedNonprofits = useMemo(() => {
    // First, filter out nonprofits with no services
    let filtered = nonprofits.filter((nonprofit) => {
      return (
        nonprofit.services &&
        Array.isArray(nonprofit.services) &&
        nonprofit.services.length > 0
      );
    });

    // Then, if a category is selected, filter by that category
    if (form.typeCode) {
      filtered = filtered.filter((nonprofit) => {
        // Check if nonprofit has at least one service matching the selected category value
        return nonprofit.services.some(
          (service) => service.value === form.typeCode,
        );
      });
    }

    // Then sort: those with sent requests first, then those without
    const sent = filtered.filter((nonprofit) =>
      sentRecipientIds.includes(nonprofit.id),
    );
    const notSent = filtered.filter(
      (nonprofit) => !sentRecipientIds.includes(nonprofit.id),
    );
    return [...sent, ...notSent];
  }, [nonprofits, sentRecipientIds, form.typeCode]);

  const offset = currentPage * itemsPerPage;
  const currentItems = sortedNonprofits.slice(offset, offset + itemsPerPage);
  const pageCount = Math.ceil(sortedNonprofits.length / itemsPerPage);

  const handlePageClick = (event) => {
    setCurrentPage(event.selected);
  };

  return (
    <div className="flex flex-col lg:flex-row mt-8 w-[94%] mx-auto">
      <div className="lg:w-[60%]">
        <h1 className="font-popinsBold text-2xl xl:text-3xl">
          Post Event / Service
        </h1>
        <p className="text-textColor font-popinsRegular mt-1 text-xs xl:text-sm">
          Share your upcoming event or service with the community and
          volunteers.
        </p>
        <Link to={"/dashboard/manage-events"}>
          <button
            className="relative overflow-hidden py-1 px-3 border border-gray-300 text-gray-500 font-popinsRegular text-xs rounded
                             transform transition duration-300 ease-in-out
                             hover:border-gray-400 hover:text-gray-600 hover:bg-gray-50
                             active:scale-95 focus:outline-none
                             flex items-center gap-x-1.5 mt-4"
          >
            <ChevronLeft size={12} />
            <span>Back to Manage Event</span>
          </button>
        </Link>
        <form className="lg:w-[90%] my-8" onSubmit={handleSubmit}>
          <div className="flex flex-col lg:flex-row lg:justify-between">
            <div className="w-full lg:w-[49%]">
              <label className="font-popinsMedium text-sm">Event Title <span className="text-red-500">*</span></label>
              <input
                type="text"
                placeholder="Event Title"
                className={`w-full mt-2 font-popinsMedium rounded-md border-2 bg-white px-4 py-2 text-black/80 outline-none transition-all duration-300 ease-in-out
  ${
    errors.title
      ? "border-red-500 focus:border-red-500 focus:shadow-[0_0_3px_rgba(239,68,68,0.5)]"
      : "border-border focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3]"
  }`}
                value={form.title}
                onChange={handleFormChange("title")}
              />
            </div>
            <div className="w-full lg:w-[49%] mt-4 lg:mt-0">
              <label className="font-popinsMedium text-sm">
                Category <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  className={`w-full mt-2 font-popinsMedium rounded-md border-2 border-border bg-white px-4 py-2 pr-10 outline-none transition-all duration-300 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3] appearance-none cursor-pointer ${
                    errors.typeCode
                      ? "border-red-500 focus:border-red-500 focus:shadow-[0_0_3px_rgba(239,68,68,0.5)]"
                      : "border-border focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3]"
                  }`}
                  value={form.typeCode}
                  onChange={handleFormChange("typeCode")}
                  disabled={isLoadingCategories}
                >
                  <option value="" disabled className="text-gray-400">
                    {isLoadingCategories
                      ? "Loading categories..."
                      : "Select Category"}
                  </option>
                  {categories.map((category) => (
                    <option
                      key={category.id}
                      value={category.value}
                      className="text-black/80"
                    >
                      {category.name}
                    </option>
                  ))}
                </select>
                <div className="absolute right-3 top-5 text-black/50 pointer-events-none">
                  <ChevronDown size={20} />
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4">
            <label className="font-popinsMedium text-sm">Description <span className="text-red-500">*</span></label>
            <textarea
              rows="4"
              placeholder="Event Description"
              className={`w-full mt-2 font-popinsMedium rounded-md border-2 bg-white px-4 py-2 text-black/80 outline-none transition-all duration-300 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3] resize-none ${
                errors.description
                  ? "border-red-500 focus:border-red-500 focus:shadow-[0_0_3px_rgba(239,68,68,0.5)]"
                  : "border-border focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3]"
              }`}
              value={form.description}
              onChange={handleFormChange("description")}
            ></textarea>
          </div>
          <div className="flex flex-col lg:flex-row lg:justify-between mt-4 gap-y-4 lg:gap-y-0">
            <div className="w-full lg:w-[49%]">
              <label className="font-popinsMedium text-sm">Date <span className="text-red-500">*</span></label>
              <input
                type="date"
                className={`w-full mt-2 font-popinsMedium rounded-md border-2 bg-white px-4 py-2 text-black/80 outline-none transition-all duration-300 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3] cursor-pointer ${
                  errors.date
                    ? "border-red-500 focus:border-red-500 focus:shadow-[0_0_3px_rgba(239,68,68,0.5)]"
                    : "border-border focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3]"
                }`}
                value={form.date}
                onChange={handleFormChange("date")}
              />
            </div>
            <div className="w-full lg:w-[49%]">
              <label className="font-popinsMedium text-sm">Time <span className="text-red-500">*</span></label>
              <input
                type="time"
                className={`w-full mt-2 font-popinsMedium rounded-md border-2 bg-white px-4 py-2 text-black/80 outline-none transition-all duration-300 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3] cursor-pointer ${
                  errors.time
                    ? "border-red-500 focus:border-red-500 focus:shadow-[0_0_3px_rgba(239,68,68,0.5)]"
                    : "border-border focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3]"
                }`}
                value={form.time}
                onChange={handleFormChange("time")}
              />
            </div>
          </div>
          <div className="flex flex-col lg:flex-row gap-4 w-full mt-4">
            <div className="w-full lg:w-[70%]">
              <label className="font-popinsMedium text-sm">Location <span className="text-red-500">*</span></label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsLocationModalOpen(true)}
                  className={`w-full mt-2 font-popinsMedium rounded-md border-2 bg-white px-4 py-2 pr-10 text-left text-black/80 outline-none transition-all duration-300 ease-in-out cursor-pointer hover:border-[#3FC4E3] ${
                    errors.location
                      ? "border-red-500"
                      : form.location
                      ? "border-border"
                      : "border-border"
                  }`}
                >
                  {form.location ? (
                    <span className="block truncate">{form.location}</span>
                  ) : (
                    <span className="text-black/40">Click to pick location on map</span>
                  )}
                </button>
                <div
                  className="absolute right-4 top-5 cursor-pointer"
                  onClick={() => setIsLocationModalOpen(true)}
                >
                  <MapPin
                    size={20}
                    className={form.location ? "text-[#3FC4E3]" : "text-black/40"}
                  />
                </div>
              </div>
              {form.location && (
                <p className="text-xs text-black/40 font-popinsRegular mt-1 truncate">
                  {form.latitude && form.longitude
                    ? `${form.latitude.toFixed(5)}, ${form.longitude.toFixed(5)}`
                    : "No coordinates"}
                </p>
              )}
            </div>
            <div className="w-full lg:w-[30%]">
              <label className="font-popinsMedium text-sm">ZIP Code <span className="text-red-500">*</span></label>
              <div className="relative mt-2" ref={zipCodeDropdownRef}>
                <input
                  type="text"
                  placeholder="Search ZIP Code"
                  value={zipCodeSearch}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "");
                    setZipCodeSearch(val);
                    setForm((prev) => ({ ...prev, zipCode: "" }));
                    setIsZipCodeDropdownOpen(true);
                    if (errors.zipCode) setErrors((prev) => ({ ...prev, zipCode: false }));
                  }}
                  onFocus={() => setIsZipCodeDropdownOpen(true)}
                  onBlur={() => {
                    setTimeout(() => {
                      if (!form.zipCode) setZipCodeSearch("");
                    }, 200);
                  }}
                  className={`w-full font-popinsMedium rounded-md border-2 bg-white px-4 pr-10 py-2 text-black/80 outline-none transition-all duration-300 ease-in-out ${
                    errors.zipCode
                      ? "border-red-500 focus:border-red-500 focus:shadow-[0_0_3px_rgba(239,68,68,0.5)]"
                      : "border-border focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3]"
                  }`}
                />
                <ChevronDown
                  size={20}
                  className={`text-black/50 transition-transform absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none ${isZipCodeDropdownOpen ? "rotate-180" : ""}`}
                />
                {isZipCodeDropdownOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white border-2 border-border rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {(() => {
                      const filtered = tarrantCountyZipCodes.filter((z) => z.startsWith(zipCodeSearch));
                      if (filtered.length === 0) {
                        return (
                          <div className="px-4 py-2 text-sm text-black/50 font-popinsMedium">No Match Found</div>
                        );
                      }
                      return filtered.map((z) => (
                        <div
                          key={z}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setForm((prev) => ({ ...prev, zipCode: z }));
                            setZipCodeSearch(z);
                            setIsZipCodeDropdownOpen(false);
                            if (errors.zipCode) setErrors((prev) => ({ ...prev, zipCode: false }));
                          }}
                          className="px-4 py-2 text-sm font-popinsMedium text-black/50 cursor-pointer hover:bg-[#3FC4E3]/10"
                        >
                          {z}
                        </div>
                      ));
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="w-full mt-4">
            <label className="font-popinsMedium text-sm mb-2 block">
              Cover Image <span className="text-red-500">*</span>
            </label>
            {coverImageUrl && !coverImageError && (
              <div className="relative mb-4">
                <img
                  src={coverImageUrl}
                  alt="Cover preview"
                  className="w-full h-[200px] object-cover rounded-lg border-2 border-gray-200"
                  onError={() => setCoverImageError(true)}
                />
                <button
                  type="button"
                  onClick={handleRemoveCoverImage}
                  className="absolute top-2 right-2 bg-white/90 hover:bg-white rounded-full w-6 h-6 flex items-center justify-center transition-all shadow-lg backdrop-blur-sm"
                  disabled={isUploadingCoverImage || isSubmitting}
                  aria-label="Remove cover image"
                >
                  <X size={16} className="text-gray-700" />
                </button>
              </div>
            )}
            {coverImageError && (
              <div className="w-full h-[200px] bg-red-50 border-2 border-red-300 rounded-lg flex items-center justify-center mb-4">
                <p className="text-red-500 text-sm text-center px-4">
                  Image failed to load
                </p>
              </div>
            )}
            <div
              className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl px-6 bg-white transition-all duration-300 ease-in-out relative
  ${
    errors.coverImage
      ? "border-red-500"
      : "border-gray-300 hover:border-[#3FC4E3] hover:bg-[#f9feff]"
  }
  ${coverImageUrl ? "py-4" : "py-10"}
`}
              onDrop={handleCoverImageDrop}
              onDragOver={handleCoverImageDragOver}
              onDragLeave={handleCoverImageDragLeave}
            >
              <h3
                className={`font-popinsSemiBold mb-1 ${
                  coverImageUrl ? "text-base" : "text-lg"
                }`}
              >
                {coverImageUrl ? "Change Cover Image" : "Upload Cover image"}
              </h3>
              {!coverImageUrl && (
                <p className="text-sm text-gray-500 mb-4">
                  Drag and drop image here or browse
                </p>
              )}
              <label
                htmlFor="coverImageInput"
                className="cursor-pointer inline-block bg-gray-100 hover:bg-[#3FC4E3] hover:text-white transition-all duration-300 ease-in-out text-sm font-semibold px-5 py-2 rounded-lg shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {coverImageUrl ? "Change" : "Upload"}
              </label>
              <input
                id="coverImageInput"
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) {
                    handleCoverImageSelect(file);
                  }
                }}
                disabled={isSubmitting}
              />
            </div>
          </div>
          <div className="flex justify-between items-center mt-4">
            <p className="font-popinsRegular text-sm">Need Volunteers?</p>
            <label className="inline-flex items-center cursor-pointer relative pl-3">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={form.needVolunteers}
                onChange={handleFormChange("needVolunteers")}
              />
              <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-2 after:border-themeColor after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-themeColor"></div>
            </label>
          </div>
          {form.needVolunteers && (
            <div className="mt-4">
              <p className="font-popinsSemiBold mb-4 text-base">Resources</p>
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                <div className="w-full lg:flex-[3]">
                  <div className="relative">
                    <select
                      className={`w-full font-popinsMedium rounded-lg border-2 border-gray-300 bg-white px-4 py-2.5 pr-10 outline-none transition-all duration-200 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_0_3px_rgba(63,196,227,0.1)] cursor-pointer appearance-none ${
                        roleDraft.roleName ? "text-gray-900" : "text-gray-400"
                      }`}
                      value={roleDraft.roleName}
                      onChange={handleRoleDraftChange("roleName")}
                      disabled={isLoadingCategories}
                    >
                      <option value="" disabled className="text-gray-400">
                        {isLoadingCategories ? "Loading..." : "Select Type"}
                      </option>
                      {categories.map((category) => (
                        <option
                          key={category.id}
                          value={category.value}
                          className="text-gray-900"
                        >
                          {category.name}
                        </option>
                      ))}
                      <option value="OTHER" className="text-gray-900">
                        Other
                      </option>
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                      <ChevronDown size={18} />
                    </div>
                  </div>
                </div>
                <div className="w-full lg:w-auto">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Qty."
                      className="w-20 font-popinsMedium rounded-lg border-2 border-gray-300 bg-white px-3 py-2.5 text-gray-900 placeholder-gray-400 outline-none transition-all duration-200 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_0_3px_rgba(63,196,227,0.1)]"
                      value={roleDraft.countNeeded}
                      onChange={handleRoleDraftChange("countNeeded")}
                      inputMode="numeric"
                    />
                    <div className="relative">
                      <select
                        className="w-full font-popinsMedium rounded-lg border-2 border-gray-300 bg-white px-3 py-2.5 pr-8 outline-none transition-all duration-200 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_0_3px_rgba(63,196,227,0.1)] cursor-pointer appearance-none text-gray-900"
                        value={roleDraft.quantityType}
                        onChange={handleRoleDraftChange("quantityType")}
                      >
                        <option value="Unit">Unit(s)</option>
                        <option value="Person">Person(s)</option>
                      </select>
                      <div className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                        <ChevronDown size={18} />
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="relative overflow-hidden py-2.5 px-6 bg-themeColor text-white font-popinsSemiBold text-sm rounded-lg shadow-md transform transition-all duration-200 ease-in-out hover:bg-[#35b3d1] hover:shadow-lg active:scale-95 focus:outline-none focus:ring-2 focus:ring-[#3FC4E3] focus:ring-offset-2 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:shadow-md whitespace-nowrap"
                  onClick={handleAddRole}
                  disabled={
                    isSubmitting ||
                    !roleDraft.countNeeded ||
                    !roleDraft.roleName
                  }
                >
                  <Plus size={16} strokeWidth={2.5} className="text-white" />
                  <span>Add</span>
                </button>
              </div>
              <div className="mt-5">
                <label className="font-popinsMedium text-sm text-gray-700 mb-1.5 block">
                  Special Requirements
                </label>
                <textarea
                  rows="4"
                  className="w-full font-popinsMedium rounded-lg border-2 border-gray-300 bg-white px-4 py-3 text-gray-900 placeholder-gray-400 outline-none transition-all duration-200 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_0_3px_rgba(63,196,227,0.1)] resize-none"
                  value={roleDraft.specialRequirements}
                  onChange={handleRoleDraftChange("specialRequirements")}
                  placeholder="Add more details to your resource requirements"
                ></textarea>
              </div>
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm mt-5">
                <div className="flex border-b border-gray-200 bg-gray-50 px-5 py-3.5">
                  <div className="w-[20%]">
                    <p className="font-popinsSemiBold text-sm text-gray-700">
                      Type
                    </p>
                  </div>
                  <div className="w-[20%]">
                    <p className="font-popinsSemiBold text-sm text-gray-700">
                      Quantity
                    </p>
                  </div>
                  <div className="w-[60%] flex justify-between items-center pr-4">
                    <p className="font-popinsSemiBold text-sm text-gray-700">
                      Special Requirements
                    </p>
                    <span className="sr-only">Actions</span>
                  </div>
                </div>
                {roles.length === 0 ? (
                  <div className="py-8 text-center text-gray-400 font-popinsRegular text-sm">
                    No volunteer roles added yet.
                  </div>
                ) : (
                  roles.map((role, index) => (
                    <div
                      key={role.id}
                      className={`flex border-b last:border-b-0 border-gray-100 text-gray-700 py-4 px-5 items-center hover:bg-gray-50 transition-colors ${
                        index % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                      }`}
                    >
                      <div className="w-[20%]">
                        <p className="font-popinsMedium text-sm text-gray-900">
                          {roleNameDisplay(role.roleName)}
                        </p>
                      </div>
                      <div className="w-[20%]">
                        <p className="font-popinsMedium text-sm text-gray-900">
                          {role.countNeeded}{" "}
                          {role.quantityType === "Person"
                            ? "Person(s)"
                            : "Unit(s)"}
                        </p>
                      </div>
                      <div className="w-[60%] flex justify-between items-center pr-2 gap-3">
                        <p className="font-popinsRegular text-sm text-gray-600 text-left break-words flex-1">
                          {role.specialRequirements || (
                            <span className="text-gray-400">—</span>
                          )}
                        </p>
                        <button
                          type="button"
                          className="text-red-500 hover:text-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed p-1 rounded hover:bg-red-50 flex-shrink-0"
                          onClick={() => handleRemoveRole(role.id)}
                          title="Remove role"
                          disabled={isSubmitting}
                        >
                          <MinusCircle size={18} aria-hidden />
                          <span className="sr-only">Remove role</span>
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
          <div className="w-full mt-4">
            <p className="text-base mb-2 font-popinsSemiBold">
              Supporting Files
            </p>
            <div
              className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl px-6 py-6 bg-white transition-all duration-300 ease-in-out hover:border-[#3FC4E3] hover:bg-[#f9feff]"
              onDrop={handleResourceDrop}
              onDragOver={handleResourceDragOver}
              onDragLeave={handleDragLeaveResource}
            >
              <p className="text-sm text-gray-500 mb-4">
                Drag and drop files here or browse
              </p>
              <label
                htmlFor="filesInput"
                className="cursor-pointer inline-block bg-gray-100 hover:bg-[#3FC4E3] hover:text-white transition-all duration-300 ease-in-out text-sm font-semibold px-5 py-2 rounded-lg shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Select Files
              </label>
              <input
                id="filesInput"
                type="file"
                multiple
                accept=".pdf,.doc,.docx,image/*"
                className="hidden"
                onChange={(event) => {
                  handleResourceFiles(event.target.files);
                  // Reset input to allow selecting same file again
                  event.target.value = "";
                }}
                disabled={isSubmitting || isUploadingFiles}
              />
              <p className="text-xs text-gray-400 mt-2">
                Supported: PDF, DOC, DOCX, Images (Max 3MB each)
              </p>
            </div>
            {(files.length > 0 || filesToUpload.length > 0) && (
              <div className="mt-4 space-y-2">
                {/* Existing files */}
                {files.map((file, index) => {
                  // Extract filename from URL if name is still a URL
                  let displayName = file.name;
                  if (displayName && displayName.startsWith("http")) {
                    displayName =
                      displayName.split("/").pop() || `file-${index + 1}`;
                  }
                  // Clean up filename (remove query params, etc.)
                  displayName = displayName.split("?")[0];

                  // Determine file icon component based on file type
                  const getFileIcon = (fileName) => {
                    const ext = fileName.split(".").pop()?.toLowerCase();
                    if (ext === "pdf") {
                      return <FileText size={18} className="text-red-600" />;
                    }
                    if (["doc", "docx"].includes(ext)) {
                      return <FileText size={18} className="text-blue-600" />;
                    }
                    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) {
                      return <ImageIcon size={18} className="text-green-600" />;
                    }
                    return <FileIcon size={18} className="text-gray-600" />;
                  };

                  const handleDownload = async (e) => {
                    e.preventDefault();
                    try {
                      const response = await fetch(file.url);
                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement("a");
                      link.href = url;
                      link.download = displayName;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      window.URL.revokeObjectURL(url);
                    } catch (error) {
                      console.error("Error downloading file:", error);
                      toast.error("Failed to download file. Please try again.");
                    }
                  };

                  return (
                    <div
                      key={`existing-${index}`}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="flex-shrink-0">
                          {getFileIcon(displayName)}
                        </div>
                        <span
                          className="text-sm text-gray-700 truncate flex-1 font-popinsRegular"
                          title={displayName}
                        >
                          {displayName}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={handleDownload}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 bg-themeColor text-white rounded-md hover:bg-[#3FC4E3] transition-colors text-xs font-popinsMedium disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={isSubmitting || isUploadingFiles}
                          title={`Download ${displayName}`}
                        >
                          <Download size={14} />
                          <span>Download</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveExistingFile(index)}
                          className="text-red-500 hover:text-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={isSubmitting || isUploadingFiles}
                          title="Remove file"
                        >
                          <MinusCircle size={18} aria-hidden />
                          <span className="sr-only">Remove file</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
                {/* Files to upload */}
                {filesToUpload.map((file, index) => {
                  // Determine file icon component based on file type
                  const getFileIcon = (fileName) => {
                    const ext = fileName.split(".").pop()?.toLowerCase();
                    if (ext === "pdf") {
                      return <FileText size={18} className="text-red-600" />;
                    }
                    if (["doc", "docx"].includes(ext)) {
                      return <FileText size={18} className="text-blue-600" />;
                    }
                    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) {
                      return <ImageIcon size={18} className="text-green-600" />;
                    }
                    return <FileIcon size={18} className="text-gray-600" />;
                  };

                  return (
                    <div
                      key={`upload-${index}`}
                      className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="flex-shrink-0">
                          {getFileIcon(file.name)}
                        </div>
                        <span
                          className="text-sm text-gray-700 truncate flex-1 font-popinsRegular"
                          title={file.name}
                        >
                          {file.name}
                        </span>
                        <span className="text-xs text-gray-500 ml-2 whitespace-nowrap">
                          ({formatFileSize(file.size)})
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveFileToUpload(index)}
                        className="text-red-500 hover:text-red-600 transition-colors ml-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={isSubmitting || isUploadingFiles}
                        title="Remove file"
                      >
                        <MinusCircle size={18} aria-hidden />
                        <span className="sr-only">Remove file</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="mt-8">
            <h4 className="font-popinsSemiBold text-lg">Content Preview</h4>
            <div className="flex items-center mt-5">
              <div className="w-[50%]">
                {coverImageUrl ? (
                  !previewImageError ? (
                    <img
                      src={coverImageUrl}
                      alt="Event cover preview"
                      className="w-full h-[150px] object-cover rounded-lg"
                      onError={() => setPreviewImageError(true)}
                    />
                  ) : (
                    <div className="w-full h-[150px] bg-red-50 border-2 border-red-300 rounded-lg flex items-center justify-center">
                      <p className="text-red-500 text-xs text-center px-4">
                        Image failed to load
                      </p>
                    </div>
                  )
                ) : (
                  <div className="w-full h-[150px] bg-gray-100 rounded-lg flex items-center justify-center">
                    <p className="text-gray-400 text-sm text-center px-4">
                      No image available
                    </p>
                  </div>
                )}
              </div>
              <div className="w-[50%] pl-5">
                <p className="font-popinsMedium text-base">
                  {form.title || "Event Title"}
                </p>
                <p className="font-popinsRegular text-sm">
                  {form.typeCode
                    ? categories.find((cat) => cat.value === form.typeCode)
                        ?.name || "Category"
                    : "Category"}
                </p>
                <p className="font-popinsRegular text-sm text-gray-500">
                  {form.date && form.time
                    ? (() => {
                        const dateObj = new Date(`${form.date}T${form.time}`);
                        if (!isNaN(dateObj.getTime())) {
                          const month = String(dateObj.getMonth() + 1).padStart(
                            2,
                            "0",
                          );
                          const day = String(dateObj.getDate()).padStart(
                            2,
                            "0",
                          );
                          const year = dateObj.getFullYear();
                          const hours = String(dateObj.getHours()).padStart(
                            2,
                            "0",
                          );
                          const minutes = String(dateObj.getMinutes()).padStart(
                            2,
                            "0",
                          );
                          return `${month}/${day}/${year} ${hours}:${minutes}`;
                        }
                        return "mm/dd/yyyy 00:00";
                      })()
                    : "mm/dd/yyyy 00:00"}
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-x-5 mt-3">
            <button
              type="button"
              onClick={handleSaveDraft}
              className="relative overflow-hidden py-3 bg-[#F0F5F2] text-black font-popinsSemiBold text-sm lg:text-base mt-8 rounded-md shadow-lg transform transition duration-300 ease-in-out hover:bg-gray-200 hover:scale-105 hover:shadow-[0_0_20px_rgba(63,196,227,0.2)] active:scale-95 active:shadow-inner focus:outline-none px-5 disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={
                isSubmitting || isUploadingCoverImage || isUploadingFiles
              }
            >
              <span>Save Draft</span>
              <span className="absolute inset-0 rounded-full bg-[#F0F5F2] opacity-0 active:opacity-10 transition-opacity duration-300"></span>
            </button>
            <button
              type="submit"
              className="relative overflow-hidden py-3 bg-themeColor text-white font-popinsSemiBold text-sm lg:text-base mt-8 rounded-md shadow-lg transform transition duration-300 ease-in-out hover:bg-[#3FC4E3] hover:scale-105 hover:shadow-[0_0_20px_rgba(63,196,227,0.6)] active:scale-95 active:shadow-inner focus:outline-none px-5 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              disabled={
                isSubmitting || isUploadingCoverImage || isUploadingFiles
              }
              aria-busy={
                isSubmitting || isUploadingCoverImage || isUploadingFiles
              }
            >
              {(isSubmitting || isUploading) && (
                <Loader2 size={18} className="animate-spin" />
              )}
              <span>{actionInProgressLabel}</span>
              <span className="absolute inset-0 rounded-full bg-white opacity-0 active:opacity-10 transition-opacity duration-300"></span>
            </button>
          </div>
        </form>
      </div>
      <div className="lg:w-[40%]">
        {/* Co-Hosting Options Toggle */}
        <div className="mt-8">
          <p className="font-popinsSemiBold">Co-Hosting Options</p>
          <div className="flex justify-between items-center mt-4">
            <p className={`font-popinsRegular text-sm ${!collabRequestsEnabled ? "text-gray-400" : ""}`}>Invite NGO</p>
            <div className="relative group">
              <label className={`inline-flex items-center relative pl-3 ${collabRequestsEnabled ? "cursor-pointer" : "cursor-not-allowed opacity-40 pointer-events-none"}`}>
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={form.inviteNgoMunicipality}
                  onChange={handleFormChange("inviteNgoMunicipality")}
                  disabled={!collabRequestsEnabled}
                />
                <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-2 after:border-themeColor after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-themeColor"></div>
              </label>
              {!collabRequestsEnabled && (
                <div className="absolute right-0 bottom-full mb-2 w-56 bg-gray-800 text-white text-xs font-popinsRegular rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                  You have disabled collaboration requests. Go to{" "}
                  <span className="font-popinsSemiBold">User Settings</span> to enable.
                  <div className="absolute right-4 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Collaboration Workspace - Show below toggle when enabled */}
        {form.inviteNgoMunicipality && (
          <div className="mt-6">
            <h3 className="text-2xl font-popinsBold">
              Collaboration Workspace
            </h3>
            <p className="text-textColor font-popinsRegular mt-1 text-xs xl:text-sm">
              For your event collaborate with organizations
            </p>
            {!eventId && mode !== "edit" && (
              <p className="text-textColor font-popinsRegular mt-1 text-xs xl:text-sm">
                (you will be able to send collaboration requests after you
                create the event)
              </p>
            )}
            <div>
              {isLoadingNonprofits ? (
                <p className="text-gray-500 text-center mt-8">
                  Loading nonprofits...
                </p>
              ) : currentItems.length > 0 ? (
                currentItems.map((item) => (
                  <div
                    className="flex flex-col-reverse lg:flex-row justify-between mt-8"
                    key={item.id}
                  >
                    <div className="w-full lg:w-[65%] pr-4">
                      <h3 className="font-popinsSemiBold text-lg pb-1">
                        {item.organizationName}
                      </h3>
                      {item.address && (
                        <p className="font-popinsRegular text-sm text-black/50">
                          {item.address}
                        </p>
                      )}
                      {item.services &&
                        Array.isArray(item.services) &&
                        item.services.length > 0 && (
                          <div className="flex gap-1.5 mt-3 items-center flex-wrap">
                            {item.services.slice(0, 3).map((service, index) => (
                              <div
                                key={service.id || index}
                                className="bg-gray-100 text-gray-700 font-popinsMedium text-xs px-2 py-0.5 rounded-md whitespace-nowrap flex-shrink-0"
                              >
                                {service.name}
                              </div>
                            ))}
                            {item.services.length > 3 && (
                              <div className="relative group">
                                <div className="bg-gray-100 text-gray-700 font-popinsMedium text-xs px-2 py-0.5 rounded-md whitespace-nowrap flex-shrink-0 cursor-pointer hover:bg-gray-200 transition-colors">
                                  +{item.services.length - 3}
                                </div>
                                <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-50 bg-gray-800 text-white text-xs rounded-md px-3 py-2 shadow-xl max-w-xs min-w-[200px]">
                                  <div className="font-popinsMedium mb-2 text-white">
                                    Additional Services:
                                  </div>
                                  <div className="space-y-1">
                                    {item.services
                                      .slice(3)
                                      .map((service, index) => (
                                        <div
                                          key={
                                            service.id || `remaining-${index}`
                                          }
                                          className="font-popinsRegular text-gray-200"
                                        >
                                          • {service.name}
                                        </div>
                                      ))}
                                  </div>
                                  <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      <div className="mt-3">
                        {sentRecipientIds.includes(item.id) ? (
                          <button
                            disabled
                            className="relative overflow-hidden py-2 bg-[#9C9C9C] text-white font-popinsSemiBold text-sm rounded-md shadow-lg cursor-not-allowed opacity-60 flex items-center px-4 gap-x-2"
                          >
                            <span>Sent</span>
                            <Check size={18} />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleOpenModal(item)}
                            disabled={!eventId && mode !== "edit"}
                            className="relative overflow-hidden py-2 bg-themeColor text-white font-popinsSemiBold text-sm rounded-md shadow-lg transform transition duration-300 ease-in-out hover:bg-[#3FC4E3] hover:scale-105 hover:shadow-[0_0_20px_rgba(63,196,227,0.6)] active:scale-95 active:shadow-inner focus:outline-none px-4 gap-x-2 disabled:opacity-60 disabled:cursor-not-allowed flex items-center"
                          >
                            <span>Send Request</span>
                            <Check size={18} />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="w-full lg:w-[35%] h-36 rounded-lg overflow-hidden bg-gray-100 relative">
                      {item.profilePictureUrl ? (
                        <>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <Loader2 className="w-6 h-6 animate-spin text-themeColor" />
                          </div>
                          <img
                            src={item.profilePictureUrl}
                            alt={item.organizationName}
                            className="relative w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = "/assets/images/broken.png";
                            }}
                          />
                        </>
                      ) : (
                        <img
                          src="/assets/images/broken.png"
                          alt={item.organizationName}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center mt-8">
                  No nonprofits available
                </p>
              )}
              {sortedNonprofits.length > itemsPerPage && (
                <div className="flex justify-center border-t py-6 border-gray-200 font-popinsMedium">
                  <ReactPaginate
                    previousLabel={"<"}
                    nextLabel={">"}
                    breakLabel={"..."}
                    onPageChange={handlePageClick}
                    pageCount={pageCount}
                    containerClassName="flex space-x-2"
                    pageClassName="px-3 py-1 border rounded-md cursor-pointer"
                    activeClassName="bg-themeColor text-white"
                    previousClassName="px-3 py-1 border rounded-md cursor-pointer"
                    nextClassName="px-3 py-1 border rounded-md cursor-pointer"
                    disabledClassName="opacity-50 cursor-not-allowed"
                  />
                </div>
              )}
            </div>
            {isModalOpen && selectedNonprofitForModal && (
              <SendCollaborationRequestModal
                isOpen={isModalOpen}
                onClose={() => {
                  setIsModalOpen(false);
                  setSelectedNonprofitForModal(null);
                }}
                nonprofit={selectedNonprofitForModal}
                eventId={eventId || initialValues?.id}
                onSuccess={handleModalSuccess}
                categories={categories}
              />
            )}
          </div>
        )}

        {/* Collaboration Requests as Tasks - Only show in edit mode and when co-hosting is enabled */}
        {form.inviteNgoMunicipality &&
          (eventId || (mode === "edit" && initialValues?.id)) && (
            <section className="mt-8">
              <h3 className="text-lg font-popinsSemiBold mb-5">
                Collaboration Requests
              </h3>
              <div className="overflow-x-auto rounded-lg border border-gray-300 panelScroll">
                <table className="min-w-full bg-white">
                  <thead>
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-popinsRegular">
                        Resource
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-popinsRegular">
                        Quantity
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-popinsRegular">
                        Assign To
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-popinsRegular">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoadingRequests ? (
                      <tr>
                        <td
                          colSpan="4"
                          className="px-4 py-6 text-center text-black/50 font-popinsRegular text-sm"
                        >
                          Loading...
                        </td>
                      </tr>
                    ) : collaborationRequests.length === 0 ? (
                      <tr>
                        <td
                          colSpan="4"
                          className="px-4 py-6 text-center text-black/50 font-popinsRegular text-sm"
                        >
                          No collaboration requests sent for this event.
                        </td>
                      </tr>
                    ) : (
                      <>
                        {/* Show collaboration requests as tasks */}
                        {collaborationRequests
                          .map((recipient) => {
                            // If recipient has tasks, show each task as a row
                            // Otherwise, show the collaboration request itself as a row
                            const tasks = recipient.tasks || [];

                            if (tasks.length > 0) {
                              // Show each task with the recipient's status
                              return tasks.map((task) => (
                                <tr
                                  className="border-t"
                                  key={`${recipient.id}-${task.id}`}
                                >
                                  <td className="px-4 py-4 text-sm font-popinsRegular">
                                    {task.taskName}
                                  </td>
                                  <td className="px-4 py-4 text-sm font-popinsRegular">
                                    {task.countNeeded || "—"}
                                  </td>
                                  <td className="px-4 py-4 text-sm font-popinsRegular text-black/60">
                                    {recipient.nonprofit?.organizationName ||
                                      "N/A"}
                                  </td>
                                  <td className="px-4 py-4 font-popinsRegular">
                                    <div
                                      className={`${getStatusColor(
                                        recipient.status,
                                      )} flex justify-center rounded-lg px-7 py-2 font-popinsMedium text-sm text-white`}
                                    >
                                      {getStatusText(recipient.status)}
                                    </div>
                                  </td>
                                </tr>
                              ));
                            } else {
                              // No tasks, show the collaboration request itself
                              return (
                                <tr className="border-t" key={recipient.id}>
                                  <td className="px-4 py-4 text-sm font-popinsRegular">
                                    Collaboration Request
                                  </td>
                                  <td className="px-4 py-4 text-sm font-popinsRegular">
                                    —
                                  </td>
                                  <td className="px-4 py-4 text-sm font-popinsRegular text-black/60">
                                    {recipient.nonprofit?.organizationName ||
                                      "N/A"}
                                  </td>
                                  <td className="px-4 py-4 font-popinsRegular">
                                    <div
                                      className={`${getStatusColor(
                                        recipient.status,
                                      )} flex justify-center rounded-lg px-7 py-2 font-popinsMedium text-sm text-white`}
                                    >
                                      {getStatusText(recipient.status)}
                                    </div>
                                  </td>
                                </tr>
                              );
                            }
                          })
                          .flat()}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          )}
      </div>

      <LocationPickerModal
        isOpen={isLocationModalOpen}
        onClose={() => setIsLocationModalOpen(false)}
        onConfirm={({ location, latitude, longitude }) => {
          setForm((prev) => ({ ...prev, location, latitude, longitude }));
          if (errors.location) setErrors((prev) => ({ ...prev, location: false }));
        }}
        initialLocation={form.location}
        initialLat={form.latitude}
        initialLng={form.longitude}
      />
    </div>
  );
};

export default PostEventForm;
